import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { CONTACT_SUPPORT_URL } from '../../../../config/constants';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './ResetPasswordSuccessRedesign.module.scss';

interface ResetPasswordSuccessRedesignProps extends WizardChildProps {
  /** Overrides closing the portal / stepping back (used by the /native flow). */
  onGoBack?: () => void;
  /** Overrides the default "open the sign-up wizard" navigation (used by the /native flow). */
  onOpenSignUp?: () => void;
}

export const ResetPasswordSuccessRedesign = observer(function ResetPasswordSuccessRedesign(
  props: ResetPasswordSuccessRedesignProps,
) {
  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const contactSupport = () => {
    window.open(CONTACT_SUPPORT_URL, '_blank', 'noopener,noreferrer');
    void analytics.sendEvent('reset_password', {
      event_action: 'click',
      event_category: 'sign_up',
      event_type: 'reset_password',
      event_value: 'contact_support',
      button_text: 'contact support',
    });
  };

  const handleOpenSignUp = () => {
    if (props.onOpenSignUp) {
      props.onOpenSignUp();
      return;
    }

    openAuthWizard('signUpWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
    void analytics.sendEvent('signup_flow', {
      event_action: 'click',
      event_category: 'sign_up',
    });
  };

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="reset-password-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="reset-password-back-btn"
        headerLeftElement={<HeaderLogoElement showLogo={isDesktop} />}
        title={t('auth.resetPassword')}
        {...(isDesktop
          ? { onClose: props.onGoBack ?? portalStore.closePortal }
          : { onGoBack: props.onGoBack ?? props.prevStep })}
      />
      <div className={styles.containerContent}>
        <div className={styles.rowLineContainer} data-testid="reset-success-row-line">
          <Trans
            components={{
              title: <span className={styles.rowLineTitle} />,
              body: <span className={styles.rowLineText} />,
            }}
            i18nKey="auth.passwordResetEmailSentRow"
          />
        </div>

        <div className={styles.contactSupportButtonWrapper}>
          <Button
            btnType="purple"
            className={styles.contactSupportButton}
            dataTestId="reset-password-support-btn"
            onClick={contactSupport}
          >
            {t('common.contactSupportUpperCase')}
          </Button>
        </div>

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="reset-success-redesign-signup-line"
        >
          <Trans
            components={{
              question: <span />,
              signup: <span className={styles.signUpLink} data-testid="lobby-signup-btn" onClick={handleOpenSignUp} />,
            }}
            i18nKey="auth.noAccountYetSignUp"
          />
        </div>
      </div>
    </RedesignContainer>
  );
});
